import { AccessGate, useCan } from '../../shared/access/AccessControls';
import React, { useEffect, useMemo, useState } from 'react';
import Wrapper from '../../shared/components/Wrapper';
import { Button, Divider, Text } from '@chakra-ui/react';
import { integrationDefinitionsApi } from '../../store/services/IntegrationDefinitionsService';
import { Profile } from '../../shared/types/profile';
import IntegrationBindingRow from '../companies/components/AttachedRoleRow';
import { StyledRolesWrapper } from './styled';
import { profilesApi } from '../../store/services/ProfilesService';

const ProfileIntegrationBindingsEditor = ({ profile }: { profile: Profile }) => {
  const canRead = useCan('ADMIN_ATTACHED_ROLE_READ');
  const canListDefinitions = useCan('ADMIN_ROLE_LIST');
  const roles = useMemo(() => profile?.attachedRoles ?? [], [profile]);
  const { currentData, isFetching, isError, refetch } =
    profilesApi.useGetProfileAttachedRolesQuery(
      { profileId: profile?.id ?? '' },
      { skip: !canRead || !profile?.id, refetchOnMountOrArgChange: true },
    );
  // Never use attributes from the profile response, including during rollout.
  // currentData excludes another profile's cache; refetch/error data cannot save.
  const configurations =
    !isFetching &&
    !isError &&
    currentData &&
    Array.isArray(currentData.attachedRoles)
      ? currentData.attachedRoles
      : [];

  const [trigger] = integrationDefinitionsApi.useLazyGetRolesQuery();

  useEffect(() => {
    if (canListDefinitions) trigger({});
  }, [trigger, canListDefinitions]);

  return (
    <Wrapper>
      {(!profile && <p>Loading profile info...</p>) || (
        <>
          <Text mb={'1rem'}>
            <b>Profile integration bindings</b>
          </Text>

          {isFetching && (
            <Text role="status">Loading integration configuration...</Text>
          )}
          {isError && (
            <Text role="alert">
              Could not load integration configuration. Saving is disabled.
            </Text>
          )}
          <Button
            size="sm"
            mb="1rem"
            isDisabled={isFetching}
            onClick={() => refetch()}
          >
            Reload configuration
          </Button>

          <StyledRolesWrapper>
            {(!!roles.length &&
              roles.map((role) => (
                <React.Fragment key={role.id}>
                  <IntegrationBindingRow
                    profileId={profile.id}
                    role={role}
                    configuration={configurations.find(
                      (entry) => entry.id === role.id,
                    )}
                  />
                  <Divider />
                </React.Fragment>
              ))) || (
              <>
                <p>No integration bindings</p>
                <Divider />
              </>
            )}

            <IntegrationBindingRow profileId={profile.id} />
          </StyledRolesWrapper>
        </>
      )}
    </Wrapper>
  );
};

const ProfileIntegrationBindings = ({ profile }: { profile: Profile }) => {
  const [editing, setEditing] = useState(false);
  return <Wrapper><>
    <Text as="h2">Integration bindings</Text>
    {!editing && (profile?.attachedRoles || []).map(binding => <div key={binding.id}><b>{binding.role.name}</b><p>{binding.role.description}</p></div>)}
    <AccessGate action="ADMIN_ATTACHED_ROLE_READ">
      <Button onClick={() => setEditing(value => !value)}>{editing ? 'Close integration editor' : 'Edit integration bindings'}</Button>
      {editing && <ProfileIntegrationBindingsEditor profile={profile} />}
    </AccessGate>
  </></Wrapper>;
};
export default ProfileIntegrationBindings;
